import React, { useRef, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Rocket, Sparkles, MailOpen } from "lucide-react";

/* P1 (2026-08) — Referral onboarding.
 * Two pieces, backend-truth-only:
 *   1. <ReferralInviteHero/> — the pre-registration landing state after a
 *      valid referral link. Feels like a personal, exclusive invitation
 *      ("Du wurdest eingeladen!"), NOT a generic signup screen. Neutral
 *      (NO inviter name / PII). The referral code stays persisted by the
 *      caller; the CTA just scrolls/focuses the signup form.
 *   2. <ReferralSuccessOverlay/> — shown ONLY when register() returned
 *      applied_referral=true (i.e. the trial was actually granted).
 * Dark-mode native, premium DealAlert emerald glow + a subtle one-shot
 * confetti burst, honours prefers-reduced-motion, uses framer-motion. */

const CONFETTI = [
  { x: -78, y: -50, c: "bg-emerald-400", d: 0.02 },
  { x: 72, y: -58, c: "bg-sky-400", d: 0.06 },
  { x: -104, y: 6, c: "bg-emerald-300", d: 0.1 },
  { x: 98, y: 2, c: "bg-amber-300", d: 0.14 },
  { x: -52, y: 60, c: "bg-sky-300", d: 0.18 },
  { x: 60, y: 66, c: "bg-emerald-400", d: 0.22 },
  { x: 8, y: -74, c: "bg-amber-200", d: 0.26 },
];

// Fire the celebration only once per browser session (survives typing /
// re-renders / back-navigation to the same tab).
function useOneShotCelebration() {
  return useState(() => {
    if (typeof window === "undefined") return true;
    try {
      if (sessionStorage.getItem("da_invite_celebrated")) return false;
      sessionStorage.setItem("da_invite_celebrated", "1");
      return true;
    } catch {
      return true;
    }
  })[0];
}

export function ReferralInviteHero({ t, onCta, refCode }) {
  const reduce = useReducedMotion();
  const celebrate = useOneShotCelebration();

  return (
    <motion.section
      data-testid="referral-invite-hero"
      data-ref-code={refCode || undefined}
      initial={reduce ? { opacity: 0 } : { opacity: 0, y: 10 }}
      animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className="relative overflow-hidden rounded-3xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/[0.14] via-zinc-950/30 to-zinc-950/60 px-5 py-7 text-center sm:px-8 sm:py-9"
    >
      {/* radial glow */}
      <motion.div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 -top-10 h-52 w-52 -translate-x-1/2 rounded-full bg-emerald-500/25 blur-3xl"
        initial={{ opacity: 0.15, scale: 0.6 }}
        animate={
          reduce
            ? { opacity: 0.3 }
            : { opacity: [0.15, 0.5, 0.32], scale: [0.6, 1.1, 1] }
        }
        transition={{ duration: 1.8, ease: "easeOut" }}
      />

      {/* one-shot confetti */}
      {celebrate && !reduce && (
        <div aria-hidden="true" className="pointer-events-none absolute left-1/2 top-12">
          {CONFETTI.map((p, i) => (
            <motion.span
              key={i}
              className={`absolute h-2 w-2 rounded-full ${p.c}`}
              initial={{ opacity: 0, x: 0, y: 0, scale: 0.5 }}
              animate={{ opacity: [0, 1, 0], x: p.x, y: p.y, scale: [0.5, 1, 0.7] }}
              transition={{ duration: 1.5, delay: p.d, ease: "easeOut" }}
            />
          ))}
        </div>
      )}

      <div className="relative">
        {/* invite badge */}
        <div
          data-testid="referral-invite-badge"
          className="mx-auto mb-5 inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.18em] text-emerald-300"
        >
          <Sparkles className="h-3.5 w-3.5" />
          {t("register.invite_badge")}
        </div>

        <motion.div
          className="relative mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl border border-emerald-500/40 bg-emerald-500/15 text-emerald-300"
          initial={reduce ? { opacity: 0 } : { rotate: -10, scale: 0.75 }}
          animate={reduce ? { opacity: 1 } : { rotate: 0, scale: 1 }}
          transition={{ type: "spring", stiffness: 240, damping: 15, delay: 0.08 }}
        >
          <MailOpen className="h-7 w-7" strokeWidth={1.75} />
        </motion.div>

        <h1
          data-testid="referral-invite-title"
          className="text-2xl font-semibold tracking-tight text-zinc-50 sm:text-3xl"
        >
          {t("register.invite_hero_title")}
        </h1>
        <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-zinc-300 sm:text-base">
          {t("register.invite_hero_subline")}
        </p>
        <p className="mt-2 text-sm font-medium text-emerald-300/90">
          {t("register.invite_hero_note")}
        </p>

        <button
          type="button"
          onClick={onCta}
          data-testid="referral-invite-cta"
          className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-emerald-500 px-6 py-3 text-sm font-semibold text-zinc-950 shadow-lg shadow-emerald-500/20 transition-all hover:bg-emerald-400 hover:shadow-emerald-500/30 sm:w-auto sm:px-8"
        >
          <Rocket className="h-4 w-4" />
          {t("register.invite_hero_cta")}
        </button>
        <p className="mt-3 text-xs text-zinc-500">
          {t("register.invite_hero_micro")}
        </p>
      </div>
    </motion.section>
  );
}

export function ReferralSuccessOverlay({ t, open, onClose }) {
  const reduce = useReducedMotion();

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="referral-success-overlay"
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          <div className="absolute inset-0 bg-zinc-950/80 backdrop-blur-sm" />
          <motion.div
            role="dialog"
            aria-modal="true"
            initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.9, y: 12 }}
            animate={reduce ? { opacity: 1 } : { opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-sm overflow-hidden rounded-3xl border border-emerald-500/30 bg-zinc-950 px-6 py-8 text-center shadow-2xl"
          >
            <motion.div
              aria-hidden="true"
              className="pointer-events-none absolute left-1/2 top-6 h-40 w-40 -translate-x-1/2 rounded-full bg-emerald-500/25 blur-3xl"
              initial={{ opacity: 0.2, scale: 0.6 }}
              animate={
                reduce
                  ? { opacity: 0.3 }
                  : { opacity: [0.2, 0.55, 0.35], scale: [0.6, 1.15, 1] }
              }
              transition={{ duration: 1.6, ease: "easeOut" }}
            />

            {!reduce &&
              CONFETTI.map((p, i) => (
                <motion.span
                  key={i}
                  aria-hidden="true"
                  className={`absolute left-1/2 top-10 h-2 w-2 rounded-full ${p.c}`}
                  initial={{ opacity: 0, x: 0, y: 0, scale: 0.5 }}
                  animate={{ opacity: [0, 1, 0], x: p.x, y: p.y, scale: [0.5, 1, 0.7] }}
                  transition={{ duration: 1.4, delay: p.d, ease: "easeOut" }}
                />
              ))}

            <motion.div
              className="relative mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl border border-emerald-500/40 bg-emerald-500/15 text-emerald-300"
              initial={reduce ? { opacity: 0 } : { rotate: -12, scale: 0.7 }}
              animate={reduce ? { opacity: 1 } : { rotate: 0, scale: 1 }}
              transition={{ type: "spring", stiffness: 260, damping: 16, delay: 0.05 }}
            >
              <Rocket className="h-7 w-7" strokeWidth={2} />
            </motion.div>

            <h2
              className="text-xl font-semibold tracking-tight text-zinc-50"
              data-testid="referral-success-title"
            >
              {t("register.referral_success_title")}
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-zinc-300">
              {t("register.referral_success_body")}
            </p>

            <button
              type="button"
              onClick={onClose}
              data-testid="referral-success-cta"
              className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-medium text-zinc-950 transition-colors hover:bg-emerald-400"
            >
              {t("register.referral_success_cta")}
              <span aria-hidden="true">→</span>
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
